import { formatMoney } from "@shopify/theme-currency";

import {
    disableBodyScroll,
    enableBodyScroll
} from '@/helpers/bodyScrollLockWrapper';

import { EVENT_MINICART_PRODUCTS_REMOVE, EVENT_MINICART_UPDATED } from "@/store/events";

const selectors = {
    container: '[data-ck-upsells-modal]',
    opener: '[data-ck-upsells-open]',
    closer: '[data-ck-upsells-close]',
    tabsSlider: '[data-ck-upsells-tabs-slider]',
    tab: '[data-ck-upsell-tab]',
    panel: '[data-ck-upsell-panel]',
    selectBtn: '[data-ck-upsells-toggle-variant]',
    variant: '[data-ck-upsells-variant]',
    product: '[data-ck-upsells-product]',
    selectedCounter: '[data-ck-upsells-product-selected]',
    selectedItemsSlider: '[data-ck-upsells-selected-items-slider]',
    selectedItemsNode: '[data-ck-upsells-selected-items]',
    removeVariant: '[data-ck-upsells-remove-variant]',
    totalSelectedCount: '[data-ck-upsells-total-selected]',
    submitBtn: '[data-ck-upsells-submit]',
    badge: '[data-ck-upsells-badge]',
    selectedItemsFinal: '[data-ck-upsells-final-selected-items]',
    discard: '[data-ck-upsell-discard]',
    payment: '[data-ck-upsells-payment]',
    variantJSON: '[data-ck-upsells-variant-json]',
    step: '[data-ck-upsells-step]',
    recurringPrice: '[data-recurring-orders-total]',
    limit: '[data-ck-upsells-total-limit]'
};

const moneyFormat = window.theme.moneyFormat;

class CkUpsells {
    container = null;

    selectedItemsSlider = null;

    limit = 0;

    openedClass = 'account-dashboard-modal--opened';

    defaultItemSlide = `
        <li class="swiper-slide ck-upsells-modal__selected-item">
            <div class="ck-upsells-modal__selected-item-image">
                <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                    <path d="M9 2.5C9 2.22386 8.77614 2 8.5 2C8.22386 2 8 2.22386 8 2.5V7.5H3C2.72386 7.5 2.5 7.72386 2.5 8C2.5 8.27614 2.72386 8.5 3 8.5H8V13.5C8 13.7761 8.22386 14 8.5 14C8.77614 14 9 13.7761 9 13.5V8.5H14C14.2761 8.5 14.5 8.27614 14.5 8C14.5 7.72386 14.2761 7.5 14 7.5H9V2.5Z" fill="#888888"></path>
                </svg>
            </div>
            <p class="ck-upsells-modal__selected-item-title"></p>
        </li>
    `;

    constructor(container) {
        if (!container) {
            return;
        }

        this.container = container;

        this.init();
    }

    async init() {
        this.teleport();
        this.initLimit();
        this.initWindowObject();
        this.initOpeners();
        this.initClosers();
        await this.initTabsSlider();
        this.initTabs();
        this.initSelectBtns();
        await this.initSelectedItemsSlider();
        this.initRemoveVariant();
        this.initSkip();
        this.initSubmit();
        this.initDiscard();
        this.initOnCartChange();
    }

    initLimit(value = null) {
        const limit = value || this.container.dataset.ckUpsellsLimit;
        if (!limit) {
            return;
        }

        this.limit = parseInt(limit);

        const limitNode = this.container.querySelector(selectors.limit);
        if (!limitNode) {
            return;
        }

        limitNode.innerHTML = this.limit;
    }

    initWindowObject() {
        window.CkUpsells = {
            mode: 'ADD',
            confirmed: false,
            variants: [],
            cart: {
                variants: []
            }
        };
    }

    updateWindowObject(cart) {
        if (!cart || !window?.CkUpsells?.cart) {
            return;
        }

        const subscriptionUpsells = (cart.items || []).filter((item) => !!item?.properties?._subscription_upsell);
        if (!subscriptionUpsells?.length) {
            return;
        }

        window.CkUpsells.cart.variants = subscriptionUpsells.map((upsell) => {
            const selling_plan_allocations = JSON.parse(this.container.querySelector(`[data-ck-upsells-toggle-variant="${ upsell.variant_id }"]`)?.closest(selectors.variant)?.querySelector(selectors.variantJSON)?.innerHTML || {}) || {};

            if (!Object.keys(selling_plan_allocations)?.length) {
                return null;
            }

            const selling_plans = selling_plan_allocations.selling_plan_allocations || [];

            return {
                key: upsell.key,
                variant_id: upsell.variant_id,
                image: upsell.image, // todo: request small size
                title: upsell.variant_title,
                product_title: upsell.product_title,
                selling_plans,
                price: upsell.price
            };
        });
    }

    teleport() {
        document.body.appendChild(this.container);
    }

    initOpeners() {
        document.body.addEventListener('click', (event) => {
            const opener = event?.target?.closest(selectors.opener);
            if (!opener) {
                return;
            }

            event?.preventDefault();

            const mode = 'ADD';
            this.open(mode);
        });

        document.addEventListener('CkUpsells:edit', () => {
            if (!window?.CkUpsells) {
                return;
            }

            const mode = 'EDIT';

            this.open(mode);
        });
    }

    initClosers() {
        document.body.addEventListener('click', (event) => {
            const closer = event?.target?.closest(selectors.closer);
            if (!closer) {
                return;
            }

            event?.preventDefault();

            this.close();
        });
    }

    async initTabsSlider() {
        const container = this.container;
        if (!container) {
            return;
        }

        const element = container?.querySelector(selectors.tabsSlider);
        if (!element) {
            return;
        }

        const { default: Swiper, FreeMode } = await import("swiper");
        if (!Swiper) {
            return;
        }

        new Swiper(element, {
            slidesPerView: 'auto',
            spaceBetween: 24,
            maxBackfaceHiddenSlides: 1,
            freeMode: true,
            modules: [
                FreeMode
            ]
        });
    }

    initTabs() {
        const container = this.container;
        if (!container) {
            return;
        }

        const tabs = container?.querySelectorAll(selectors.tab);
        if (!tabs) {
            return;
        }

        if (!tabs?.length) {
            return;
        }

        tabs?.forEach((tab) => {
            tab?.addEventListener('click', (event) => {
                event?.preventDefault();

                const panel_id = tab?.getAttribute('aria-controls');
                if (!panel_id) {
                    return;
                }

                const panel = document.getElementById(panel_id);
                if (!panel) {
                    return;
                }

                const panels = this.container.querySelectorAll(selectors.panel);
                panels?.forEach((_panel) => {
                    _panel?.classList?.remove('ck-upsells-modal__panel--active');
                });

                panel?.classList?.add('ck-upsells-modal__panel--active');

                tabs?.forEach((_tab) => {
                    _tab.setAttribute('aria-selected', false);
                });

                tab?.setAttribute('aria-selected', true);
            });
        });
    }

    initSelectBtns() {
        const container = this.container;
        if (!container) {
            return;
        }

        const buttons = container?.querySelectorAll(selectors.selectBtn);
        if (!buttons) {
            return;
        }

        if (!buttons?.length) {
            return;
        }

        buttons?.forEach((button) => {
            button?.addEventListener('click', (event) => {
                event.preventDefault();

                if (!window?.CkUpsells) {
                    return;
                }

                const variant = button?.closest(selectors.variant);
                if (!variant) {
                    return;
                }

                const variant_id = parseInt(button?.dataset?.ckUpsellsToggleVariant || 0);
                if (!variant_id) {
                    return;
                }

                const image = button?.dataset?.variantImage || null;
                const title = button?.dataset?.variantTitle || null;
                const product_title = button?.dataset?.productTitle || null;
                const price = parseInt(button?.dataset?.variantPrice || 0);

                const adding = button?.dataset?.added === 'false';

                const selling_plan_allocations = JSON.parse(variant.querySelector(selectors.variantJSON)?.innerHTML || {}) || {};
                if (!Object.keys(selling_plan_allocations)?.length) {
                    return;
                }

                const selling_plans = selling_plan_allocations?.selling_plan_allocations || [];

                if (adding) {
                    if (
                        (window?.CkUpsells?.variants || []).includes(variant_id)
                        || (window?.CkUpsells?.variants || []).length >= this.limit
                    ) {
                        return;
                    }

                    window.CkUpsells.variants = [
                        ...(window.CkUpsells.variants || []),
                        {
                            variant_id,
                            image,
                            title,
                            product_title,
                            selling_plans,
                            price
                        }
                    ];

                    button?.setAttribute('data-added', true);
                    button.innerHTML = 'Remove';

                    variant?.classList?.add('ck-upsells-product__variant--selected');

                    this.onSelection();

                    const index = button?.closest(selectors.panel)?.dataset?.index || null;
                    if (!index) {
                        return;
                    }

                    const counter = this.container.querySelector(`[data-ck-upsells-product-selected="${ index }"]`);
                    if (!counter) {
                        return;
                    }

                    const count = parseInt(counter?.innerHTML || 0) + 1;

                    counter.innerHTML = count;

                    if (count) {
                        counter?.classList?.remove('hide');
                    } else {
                        counter?.classList?.add('hide');
                    }

                    return;
                }

                this.removeItem(variant_id);
            });
        });
    }

    setPrices() {
        const recurringPriceNodes = document.querySelectorAll(selectors.recurringPrice);
        if (!recurringPriceNodes?.length) {
            return;
        }

        const max_discount_multiplier = 0.75;

        const total_price = (window.CkUpsells.variants || []).reduce((acc, currVal) => {
            return acc + currVal.price || 0;
        }, 0) * max_discount_multiplier;

        recurringPriceNodes.forEach((el) => { el.innerHTML = `as low as ${ formatMoney(total_price, moneyFormat)?.replace('.00', '') }`; });
    }

    removeItem(variant_id) {
        if (!variant_id) {
            return;
        }

        window.CkUpsells.variants = (window?.CkUpsells?.variants || []).filter((_variant) => _variant.variant_id !== variant_id);

        this.onSelection();

        const container = this.container;
        if (!container) {
            return;
        }

        const btn = container?.querySelector(`[data-ck-upsells-toggle-variant="${ variant_id }"]`);

        if (!btn) {
            return;
        }

        btn.setAttribute('data-added', false);
        btn.innerHTML = 'Add';

        const variant = btn.closest(selectors.variant);
        if (!variant) {
            return;
        }

        variant.classList.remove('ck-upsells-product__variant--selected');

        const index = btn.closest(selectors.panel)?.dataset.index || null;
        if (!index) {
            return;
        }

        const counter = this.container.querySelector(`[data-ck-upsells-product-selected="${ index }"]`);
        if (!counter) {
            return;
        }

        const count = parseInt(counter.innerHTML || 0) - 1;

        counter.innerHTML = count;

        if (count) {
            counter.classList.remove('hide');
        } else {
            counter.classList.add('hide');
        }
    }

    onSelection() {
        const variants = window?.CkUpsells?.variants || [];
        this.updateSelectedItemsSlider(variants);
        this.updateTotalSelectedCount(variants.length);
        this.updateSubmitBtn(variants.length);
        this.updateAddBtns(variants.length);
        this.updateGeneralState(variants.length);
    }

    updateGeneralState(count) {
        if (!window?.CkUpsells) {
            return;
        }

        if (count < this.limit) {
            window.CkUpsells.confirmed = false;

            const selectedItemsFinal = document.querySelector(selectors.selectedItemsFinal);
            if (selectedItemsFinal) {
                selectedItemsFinal.innerHTML = '';
            }

            const payment = document.querySelector(selectors.payment);
            if (payment) {
                payment.classList.add('hidden');
            }

            const step = document.querySelector(selectors.step);
            if (step) {
                step.classList.remove('success');
            }
        }

        this.setPrices();
    }

    defaultItemSlidesHTML(count) {
        if (count >= this.limit || !this.defaultItemSlide) {
            return '';
        }

        const default_items_count = this.limit - count;
        if (default_items_count < 0) {
            return '';
        }

        let html = '';
        for (let i = 0; i < default_items_count; i++) {
            html += this.defaultItemSlide;
        }

        return html;
    }

    updateSelectedItemsSlider(variants) {
        if (!this.selectedItemsSlider) {
            return;
        }

        const selectedItemsNode = this.container.querySelector(selectors.selectedItemsNode);
        if (!selectedItemsNode) {
            return;
        }

        if (!variants?.length) {
            selectedItemsNode.innerHTML = this.defaultItemSlidesHTML(0);
            this.selectedItemsSlider.update();
            return;
        }

        let html = '';
        variants.forEach((variant) => {
            html += '<li class="swiper-slide ck-upsells-modal__selected-item">';

            html += `
                <button
                    class="ck-upsells-modal__selected-item-remove-btn"
                    type="button"
                    data-ck-upsells-remove-variant="${ variant.variant_id }"
                >
                    <svg class="icon-close" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18.25 1.75L1.75 18.25" stroke="black" stroke-width="2" stroke-miterlimit="10" stroke-linecap="square"></path>
                        <path d="M18.25 18.25L1.75 1.75" stroke="black" stroke-width="2" stroke-miterlimit="10" stroke-linecap="square"></path>
                    </svg>
                </button>
            `;

            html += '<div class="ck-upsells-modal__selected-item-image">';
            if (variant.image) {
                html += `
                    <img
                        src="${ variant.image }"
                        alt="${ variant.title || 'Variant Image' }"
                        width="70"
                        height="70"
                        class="absolute-img contain"
                    >
                `;
            }
            html += '</div>';

            if (variant.title) {
                html += `<p class="ck-upsells-modal__selected-item-title">${ variant.title }</p>`;
            }

            html += '</li>';
        });

        if (variants.length < this.limit) {
            html += this.defaultItemSlidesHTML(variants.length);
        }

        selectedItemsNode.innerHTML = html;
        this.selectedItemsSlider.update();
    }

    updateTotalSelectedCount(count) {
        const totalSelectedCount = this.container.querySelector(selectors.totalSelectedCount);
        if (!totalSelectedCount) {
            return;
        }

        totalSelectedCount.innerHTML = count || 0;
    }

    updateAddBtns(count) {
        const selectBtnsOfNotAdded = this.container.querySelectorAll(`${ selectors.selectBtn }[data-added="false"]`);
        if (!selectBtnsOfNotAdded?.length) {
            return;
        }

        if (count < this.limit) {
            selectBtnsOfNotAdded?.forEach((button) => {
                button.disabled = false;
            });

            return;
        }

        selectBtnsOfNotAdded?.forEach((button) => {
            button.disabled = true;
        });
    }

    updateSubmitBtn(count) {
        const container = this.container;
        if (!container) {
            return;
        }

        const submitBtn = container?.querySelector(selectors.submitBtn);
        if (!submitBtn) {
            return;
        }

        if (count < this.limit) {
            submitBtn?.classList?.add('disabled');
            submitBtn.disabled = true;

            return;
        }

        submitBtn?.classList?.remove('disabled');
        submitBtn.disabled = false;
    }

    async initSelectedItemsSlider() {
        const container = this.container;
        if (!container) {
            return;
        }

        const el = container?.querySelector(selectors.selectedItemsSlider);
        if (!el) {
            return;
        }

        const { default: Swiper } = await import("swiper");
        if (!Swiper) {
            return;
        }

        this.selectedItemsSlider = new Swiper(el, {
            slidesPerView: 1.45,
            spaceBetween: 8,
            maxBackfaceHiddenSlides: 1,
            breakpoints: {
                1025: {
                    slidesPerView: 4,
                    spaceBetween: 20,
                }
            }
        });
    }

    initRemoveVariant() {
        document.body.addEventListener('click', (event) => {
            const removeBtn = event?.target?.closest(selectors.removeVariant);
            if (!removeBtn) {
                return;
            }

            event?.preventDefault();

            const variant_id = parseInt(removeBtn?.dataset?.ckUpsellsRemoveVariant || 0);
            if (!variant_id) {
                return;
            }

            this.removeItem(variant_id);
        });
    }

    initSkip() {
        document.addEventListener('CkUpsells:skip', () => {
            if (!window?.CkUpsells) {
                return;
            }

            window.CkUpsells.confirmed = false;
            (window.CkUpsells.variants || []).forEach((variant) => {
                this.removeItem(variant.variant_id);
            });

            const selectedItemsFinal = document.querySelector(selectors.selectedItemsFinal);
            if (selectedItemsFinal) {
                selectedItemsFinal.innerHTML = '';
            }

            const payment = document.querySelector(selectors.payment);
            if (payment) {
                payment.classList.add('hidden');
            }

            /*
            * @TODO: container.
            */
            const skippedBadges = document.querySelectorAll(selectors.badge);
            if (!skippedBadges?.length) {
                return;
            }

            skippedBadges?.forEach((el) => {
                el.innerHTML = 'Skipped';
            });

            this.setPrices();
        });
    }

    initSubmit() {
        const container = this.container;
        if (!container) {
            return;
        }

        const submitBtn = container?.querySelector(selectors.submitBtn);

        submitBtn?.addEventListener('click', (event) => {
            event?.preventDefault();

            const mode = window.CkUpsells.mode || 'ADD';

            const variants = window?.CkUpsells?.variants || [];

            if (
                !submitBtn
                || variants.length !== this.limit
            ) {
                return;
            }

            if (mode === 'ADD') {
                window.CkUpsells.confirmed = true;
                /*
                * @ TODO: container
                */
                const selectedItemsFinal = document.querySelector(selectors.selectedItemsFinal);
                if (!selectedItemsFinal) {
                    return;
                }

                const variantProducts = [...new Set(variants.map((variant) => {
                    return variant.product_title || null;
                }))];

                let html = '';
                variantProducts.forEach((product) => {
                    html += '<li class="ck-upsell__selected-item">';

                    const variantsByProduct = variants.filter((variant) => {
                        return variant.product_title === product;
                    });

                    html += `<p class="ck-upsell__selected-item-text">`;

                    html += `<strong>${ product }</strong>`;

                    variantsByProduct.forEach((variant) => {
                        html += `<br>${ variant.title }`;
                    });

                    html += '</p>';

                    html += '</li>';
                });

                selectedItemsFinal.innerHTML = html;

                const badge = document.querySelector(selectors.badge);
                if (badge) {
                    badge.innerHTML = 'Special Offer';
                }

                const payment = document.querySelector(selectors.payment);
                if (payment) {
                    payment.classList.remove('hidden');
                }

                this.setPrices();

                document.dispatchEvent(new Event('CkUpsells:success'));

                this.close();

                return;
            }

            if (mode === 'EDIT') {
                /*
                *   @TODO: check for changes. Fetch new cart before
                */

                const keys = (window?.CkUpsells?.cart?.variants || []).map((variant) => {
                    return variant.key;
                });

                document.dispatchEvent(new CustomEvent(EVENT_MINICART_PRODUCTS_REMOVE, {
                    keys
                }));

                this.close();
                
            }
        });
    }

    initDiscard() {
        const discardBtn = document.querySelector(selectors.discard);
        if (!discardBtn) {
            return;
        }

        discardBtn.addEventListener('click', (e) => {
            e.preventDefault();

            if (!window?.CkUpsells) {
                return;
            }

            window.CkUpsells.confirmed = false;

            const variants = window?.CkUpsells?.variants || [];
            if (!variants) {
                return;
            }

            variants.forEach((variant) => {
                this.removeItem(variant.variant_id);
            });

            const selectedItemsFinal = document.querySelector(selectors.selectedItemsFinal);
            if (selectedItemsFinal) {
                selectedItemsFinal.innerHTML = '';
            }

            const badge = document.querySelector(selectors.badge);
            if (badge) {
                badge.innerHTML = 'Skipped';
            }

            const payment = document.querySelector(selectors.payment);
            if (payment) {
                payment.classList.add('hidden');
            }

            this.setPrices();

            document.dispatchEvent(new Event('CkUpsells:discarded'));
        });
    }

    initOnCartChange() {
        document.addEventListener(EVENT_MINICART_UPDATED, this.onCartChange.bind(this));
    }

    onCartChange(event) {
        const { detail } = event;
        if (!detail) {
            return;
        }

        const { cart } = detail;
        if (!cart) {
            return;
        }

        this.updateWindowObject(cart);
    }

    resetPopup() {
        window.CkUpsells.variants = [];
        this.initLimit();

        const selectBtns = this.container.querySelectorAll(selectors.selectBtn);
        if (!selectBtns?.length) {
            return;
        }

        selectBtns.forEach((selectBtn) => {
            selectBtn.setAttribute('data-added', false);
            selectBtn.innerHTML = 'Add';

            const variantNode = selectBtn.closest(selectors.variant);
            if (!variantNode) {
                return;
            }

            variantNode.classList.remove('ck-upsells-product__variant--selected');
        });

        const counters = this.container.querySelectorAll(selectors.selectedCounter);
        counters.forEach((counter) => { counter.innerHTML = '0'; });

        this.onSelection();
    }

    preparePopup() {
        const mode = window.CkUpsells.mode || 'ADD';
        this.resetPopup();

        if (mode !== 'EDIT') {
            return;
        }

        window.CkUpsells.variants = window.CkUpsells.cart.variants;
        this.initLimit(window.CkUpsells.variants.length);

        const selectBtns = this.container.querySelectorAll(selectors.selectBtn);
        if (!selectBtns?.length) {
            return;
        }

        if (!window.CkUpsells.variants.length) {
            return;
        }

        window.CkUpsells.variants.forEach((variant) => {
            const selectBtn = this.container.querySelector(`[data-ck-upsells-toggle-variant="${ variant.variant_id }"]`);
            if (!selectBtn) {
                return;
            }

            selectBtn.setAttribute('data-added', true);
            selectBtn.innerHTML = 'Remove';

            const variantNode = selectBtn.closest(selectors.variant);
            if (!variantNode) {
                return;
            }

            variantNode.classList.add('ck-upsells-product__variant--selected');

            const index = selectBtn.closest(selectors.panel)?.dataset.index || null;
            if (!index) {
                return;
            }

            const counter = this.container.querySelector(`[data-ck-upsells-product-selected="${ index }"]`);
            if (!counter) {
                return;
            }

            const count = parseInt(counter.innerHTML || 0) + 1;

            counter.innerHTML = count;

            if (count) {
                counter.classList.remove('hide');
            } else {
                counter.classList.add('hide');
            }
        });

        this.onSelection();
    }

    open(mode) {
        if (!mode) {
            return;
        }

        if (mode !== window.CkUpsells.mode) {
            window.CkUpsells.mode = mode;
            this.preparePopup();
        }

        this.container.classList.add(this.openedClass);

        disableBodyScroll(this.container, {
            reserveScrollBarGap: false,
            allowTouchMove(el) {
                while (el && el !== document?.body) {
                    if (el.getAttribute('data-allow-scroll') !== null) {
                        return true;
                    }

                    el = el?.parentElement;
                }

                return false;
            }
        });
    }

    close() {
        this.container.classList.remove(this.openedClass);

        if (window.CkUpsells.mode === 'EDIT') {
            return;
        }

        enableBodyScroll(this.container);
    }
}

function initCkUpsells() {
    const CkUpsellsContainers = document.querySelectorAll(selectors.container);
    if (!CkUpsellsContainers?.length) {
        return;
    }

    CkUpsellsContainers.forEach((container) => {
        new CkUpsells(container);
    });
}

document.addEventListener('DOMContentLoaded', () => {
    initCkUpsells();
});
